import Attributes from "../model/Attributes";
import AttributeValues from "../model/AttributeValues";
import { ClassDetails } from "../model/ClassDetails";
import Monster from "../model/Monster";
import SkillSet from "../model/SkillSet";
import Spell from "../model/Spell";
import ClassService from "./ClassService";
import SpellService from "./SpellService";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Abilities from "../model/Abilities";
import Speed from "../model/Speed";
import { fetchImageAsDataUrl, convertImageToWebpBlob } from "../utils/ImageUtils";

export class FantasyGroundService {
    static attrFunc = (attrName: string, attrVal: number): string => {
        const bonus = Attributes.getBonus(attrVal);
        return `
            <${attrName}>
                <bonus type="number">${bonus}</bonus>
                <savemodifier type="number">${0}</savemodifier>
                <score type="number">${attrVal}</score>
            </${attrName}>`;
    }
    static attrsFunc = (attributes: AttributeValues): string => {
        return FantasyGroundService.attrFunc('charisma', attributes.charisma)
                + FantasyGroundService.attrFunc('constitution', attributes.constitution)
                + FantasyGroundService.attrFunc('dexterity', attributes.dexterity)
                + FantasyGroundService.attrFunc('intelligence', attributes.intelligence)
                + FantasyGroundService.attrFunc('strength', attributes.strength)
                + FantasyGroundService.attrFunc('wisdom', attributes.wisdom);
    }

    static getAbility = (ability: Ability, id: string, monster: Monster): string => {
        let desc = ability.desc;
        let name = ability.name;
        if (['spellcasting','prepared spells'].includes(name?.toLowerCase())) {
            desc = '';
            name = 'Spellcasting';
            let spells = '';
            let state: 'outside' | 'header' | 'inside' | 'done' = 'outside';
            for (const line of ability.desc.split('\n')) {
                if (line.startsWith('|')) {
                    const cols = line.split('|');
                    if (cols.length === 5) {
                        if (state == 'outside' &&
                            cols[1]?.trim()?.toLowerCase() === 'level' &&
                            cols[2]?.trim()?.toLowerCase() === 'slots') {
                            // this is the header row, ignore it:
                            state = 'header';
                            continue;
                        }
                        if (state == 'header' &&
                            /^-+$/.test(cols[1]?.trim() ?? '') &&
                            /^-+$/.test(cols[2]?.trim() ?? '') &&
                            /^-+$/.test(cols[3]?.trim() ?? '')) {
                            // this is the header row separator row, ignore it too:
                            state = 'inside';
                            continue;
                        }
                        if (state == 'inside') {
                            if (cols[1].trim().toLowerCase().startsWith('cantrip')) {
                                spells += `Cantrips (at will): ${cols[3]}\\n`
                            } else {
                                spells += `${cols[1]} level (${cols[2]} slots): ${cols[3]}\\n`
                            }
                            continue;
                        }
                    }
                } else if (state == 'inside') {
                    state = 'done';
                }
                if (line?.trim()) {
                    desc += line + '\\n';
                }
            }
            desc += '\\n' + spells;
        }
        return `
            <${id}>
                <desc type="string">${FantasyGroundService.prepareText(desc, monster)}</desc>
                <name type="string">${FantasyGroundService.prepareText(name, monster)}</name>
            </${id}>`;
    }

    static getSpell = (spell: Spell, id: string, monster: Monster, spellSlots: number[]): string => {
        let name = spell.name + ' - ';
        const level = spell.level_int;
        if (level == 0) {
            name += 'Cantrip (At will)';
        } else {
            name += `${ClassService.getLevelString(level)} level`;// (${spellSlots[level]} slots)`;
        }
        let desc = [`Level: ${level}`,
                    `School: ${spell.school}`,
                    `Casting Time: ${spell.casting_time}`,
                    `Components: ${spell.components}`,
                    `Duration: ${(spell.concentration?.toLocaleLowerCase() === 'yes' ? 'Concentration, ' : '') + spell.duration}`,
                    `Range: ${spell.range}`,
                    ...spell.desc.split('\n')].join('\\n');
        return `
            <${id}>
                <desc type="string">${desc}</desc>
                <name type="string">${name}</name>
            </${id}>`;
    }

    static getSavingThrows(monster: Monster) {
        const valsArray = monster.saving_throws?.toUpperCase().split(', ');
        const proficientAttrs: string[] = [];
        const savesArray: string[] = [];

        for (const val of valsArray || []) {
            for (const attr of Attributes.attributes) {
                const attrShort = attr.toUpperCase().substring(0, 3);
                if (val.startsWith(attrShort)) {
                    proficientAttrs.push(attr);
                    const level = Attributes.getBonus(monster.attributes[attr as keyof AttributeValues]) + monster.proficiencyBonus;
                    savesArray.push(attrShort + '\u00A0' + ((level > 0) ? '+' : '') + level);
                    break;
                }
            }
        }
        return savesArray.join(", ");
    }

    static async getSpells(monster: Monster): Promise<string> {
        // Ensure spell cache is loaded
        await SpellService.fetchSpells();

        let idNum = 1;
        let response = '';
        const spellSlots = FantasyGroundService.getSpellSlotLevels(monster);
        for (const spellSlugsPreparedBySpellLevelSet of [monster.npcData.spellSlugsPreparedBySpellLevelPrimary,
                                                            monster.npcData.spellSlugsPreparedBySpellLevelSecondary]) {
            for (const spellSlugsPreparedBySpellLevel of spellSlugsPreparedBySpellLevelSet) {
                for (const spellSlug of spellSlugsPreparedBySpellLevel?.split(',') || []) {
                    let idStr = '00000' + idNum++;
                    idStr = idStr.substring(idStr.length - 5); // last 5 characters
                    const spell = SpellService.getSpellBySlug(spellSlug);
                    if (spell) {
                        response += FantasyGroundService.getSpell(spell, 'id-' + idStr, monster, spellSlots);
                    }
                }
            }
        }
        if (response) {
            response += '\n            ';
        }
        return response;
    }

    static getAbilities = (attrs: Ability[], monster: Monster, nodeName: string): string => {
        let idNum = 1;
        let response = '';
        for (const attr of attrs) {
            let idStr = '00000' + idNum++;
            idStr = idStr.substring(idStr.length - 5); // last 5 characters
            response += FantasyGroundService.getAbility(attr, 'id-' + idStr, monster);
        }
        if (response) {
            return `<${nodeName}>${response}\n        </${nodeName}>`
        }
        return `<${nodeName} />`;
    }

    static getSpellSlotLevels = (monster: Monster): number[] => {
        if (monster.npcData) {
            const primaryClass       = ClassService.getClassByName(monster.npcData.primaryClassName);
            const primaryArchetype   = ClassService.getClassByName(monster.npcData.primaryArchetypeName);
            const secondaryClass     = ClassService.getClassByName(monster.npcData.secondaryClassName);
            const secondaryArchetype = ClassService.getClassByName(monster.npcData.secondaryArchetypeName);
            const primaryClassDetails = new ClassDetails();
            primaryClassDetails.classInfo = primaryClass || null;
            primaryClassDetails.archetype = primaryArchetype || null;
            primaryClassDetails.classLevel = monster.npcData.primaryLevel;
            const secondaryClassDetails = new ClassDetails();
            secondaryClassDetails.classInfo = secondaryClass || null;
            secondaryClassDetails.archetype = secondaryArchetype || null;
            secondaryClassDetails.classLevel = monster.npcData.secondaryLevel;
            return SpellService.getSpellSlots(primaryClassDetails, secondaryClassDetails);
        }
        return [];
    }

    static getSpellSlots = (monster: Monster): string => {
        let response = '';
        const spellSlots = FantasyGroundService.getSpellSlotLevels(monster);
        for (let level = 1 ; level < 10 ; level++) {
            response += `\n            <level${level} type="number">${spellSlots[level]}</level${level}>`
        }
        return response + '\n        ';
    }

    static async getMonsterXml(monster: Monster): Promise<string> {
        if (!monster.name?.trim()) {
            alert("Monster must have a name before export!");
            return '';
        }

        // Await the async getSpells call
        const spellsXml = await FantasyGroundService.getSpells(monster);

        return `<?xml version="1.0" encoding="utf-8"?>
<!-- Monster file exported from http://greengodgames/monsterforge/ -->
<root version="5.1" dataversion="20260101" release="9|CoreRPG:7">
    <npc>
        <abilities>${FantasyGroundService.attrsFunc(monster.attributes)}
        </abilities>
        <ac type="number">${monster.armor_class}</ac>
        <actext type="string">${monster.armor_desc ? FantasyGroundService.prepareText('(' + monster.armor_desc + ')', monster) : ''}</actext>
        ${FantasyGroundService.getAbilities(monster.actions, monster, 'actions')}
        <alignment type="string">${FantasyGroundService.prepareText(monster.alignment, monster)}</alignment>
        ${FantasyGroundService.getAbilities(monster.bonus_actions, monster, 'bonusactions')}
        <conditionimmunities type="string">${FantasyGroundService.prepareText(monster.condition_immunities, monster)}</conditionimmunities>
        <cr type="string">${monster.challenge_rating}</cr>
        <damageimmunities type="string">${FantasyGroundService.prepareText(monster.damage_immunities, monster)}</damageimmunities>
        <damageresistances type="string">${FantasyGroundService.prepareText(monster.damage_resistances, monster)}</damageresistances>
        <damagethreshold type="number">0</damagethreshold>
        <hd type="string">(${FantasyGroundService.prepareText(monster.hit_dice, monster)})</hd>
        <hp type="number">${monster.hit_points}</hp>
        <innatespells />
        ${FantasyGroundService.getAbilities(monster.lair_actions, monster, 'lairactions')}
        <languages type="string">${FantasyGroundService.prepareText(monster.languages, monster)}</languages>
        ${FantasyGroundService.getAbilities(monster.legendary_actions, monster, 'legendaryactions')}
        <locked type="number">0</locked>
        <name type="string">${monster.name}</name>
        <picture type="token">campaign/images/${monster.name}.webp</picture>
        ${FantasyGroundService.getAbilities(monster.reactions, monster, 'reactions')}
        <savingthrows type="string">${FantasyGroundService.getSavingThrows(monster)}</savingthrows>
        <senses type="string">${monster.sensesObject?.customString || monster.senses}</senses>
        <size type="string">${monster.size}</size>
        <skills type="string">${FantasyGroundService.prepareText(SkillSet.getString(monster.skillSet), monster)}</skills>
        <speed type="string">${FantasyGroundService.prepareText(FantasyGroundService.formatSpeed(monster.speed), monster)}</speed>
        <spells>${spellsXml}</spells>
        <spellslots>${FantasyGroundService.getSpellSlots(monster)}</spellslots>
        <text type="formattedtext">
            ${FantasyGroundService.getHTMLDescription(monster)}
        </text>
        <token type="token">campaign/images/${monster.name}.webp</token>
        <token3Dflat type="token">campaign/images/${monster.name}.webp</token3Dflat>
        ${FantasyGroundService.getAbilities([...monster.traits, ...monster.spells], monster, 'traits')}
        <type type="string">${FantasyGroundService.prepareText(monster.type, monster)}</type>
        <xp type="number">${monster.xp}</xp>
    </npc>
</root>`.trim();
    }

    static exportMonsterToXml = async (monster: Monster) => {
        const content = await this.getMonsterXml(monster);
        // === Generate .xml file ===
        const filename = `${FantasyGroundService.sanitizeFilename(monster.name)}.xml`;
        const blob = new Blob([content], { type: "text/xml;charset=utf-8" });
        saveAs(blob, filename);

        // === Generate image file ===
        let imageSrc = monster.image?.src;

        // If image is a Storage URL, fetch and convert to data URL
        if (imageSrc && (imageSrc.startsWith('http://') || imageSrc.startsWith('https://'))) {
            console.log(`Fetching Storage image for ${monster.name}...`);
            const dataUrl = await fetchImageAsDataUrl(imageSrc);
            if (dataUrl) {
                imageSrc = dataUrl;
            }
        }

        // Export image as webp if available
        if (imageSrc) {
            const imageBlob = await convertImageToWebpBlob(imageSrc);
            if (imageBlob) {
                const imageFilename = `${FantasyGroundService.sanitizeFilename(monster.name)}.webp`;
                saveAs(imageBlob, imageFilename);
            }
        }
    }

    static exportMonsterToMod = async (monster: Monster) => {
        // === db.xml ===
        const dbXml = await this.getMonsterXml(monster);

        // === definition.xml ===
        const definitionXml = `
    <root>
        <name>${FantasyGroundService.prepareText(monster.name, monster)}</name>
        <author>Generated by Green God Games Monster Forge</author>
        <category>Monsters</category>
        <ruleset>5E</ruleset>
        <description>${FantasyGroundService.prepareText(monster.desc || "Custom monster module", monster)}</description>
    </root>`.trim();

        // === Add to ZIP ===
        const zip = new JSZip();
        zip.file("definition.xml", definitionXml);
        zip.file("db.xml", dbXml);

        // Optional thumbnail
        if (monster.image?.src) {
            zip.file("thumbnail.png", monster.image.src.split(',')[1], { base64: true });
        }

        // === Generate .mod file ===
        const content = await zip.generateAsync({ type: "blob" });
        const filename = `${FantasyGroundService.sanitizeFilename(monster.name)}.mod`;
        saveAs(content, filename);
    }

    // --- helper functions ---
    // static formatAbilities = (monster: Monster): string => {
    //     return FantasyGroundService.formatAbilitiesBlock(monster, monster.traits, 'trait') + 
    //            FantasyGroundService.formatAbilitiesBlock(monster, [...monster.actions, ...monster.spells], 'action') +
    //            FantasyGroundService.formatAbilitiesBlock(monster, monster.reactions, 'reaction') +
    //            FantasyGroundService.formatAbilitiesBlock(monster, monster.legendary_actions, 'legendaryaction') +
    //            FantasyGroundService.formatAbilitiesBlock(monster, monster.lair_actions, 'lairaction');
    // }

    // static formatAbilitiesBlock = (monster: Monster, abilities: Ability[], blockName: string): string => {
    //     if (!abilities || !abilities.length) {
    //         return '';
    //     }
    //     let blockXml = `<${blockName}s>`;
    //     abilities.forEach((ability) => {
    //         const name = FantasyGroundService.prepareText(ability.name ?? '', monster);
    //         const desc = FantasyGroundService.prepareText(ability.desc ?? '', monster);
    //         blockXml += `<${blockName}><name type="string">${name}</name><desc type="string">${desc}</desc></${blockName}>`;
    //     });
    //     blockXml += `</${blockName}s>\n`;
    //     return blockXml;
    // }

    static formatSpeed = (speed: Speed): string => {
        // adapt to your Speed object shape; this covers common fields
        const parts: string[] = [];
        if (speed.walk !== undefined && speed.walk !== null && String(speed.walk).length) parts.push(`${speed.walk} ft.`);
        if (speed.burrow) parts.push(`burrow ${speed.burrow} ft.`);
        if (speed.climb) parts.push(`climb ${speed.climb} ft.`);
        if (speed.fly) parts.push(`fly ${speed.fly} ft.`);
        if (speed.swim) parts.push(`swim ${speed.swim} ft.`);
        if (speed.hover) parts.push(`hover`);
        return parts.join(", ");
    }

    static prepareText = (source: string, monster: Monster): string => {
        source = Abilities.ReplaceTags(source, monster);
        let result = (source ?? '').replace(/\*\*([^\*]*?)\*\*/g, "$1") // Convert **bold**
                                   .replace(/__([^_]*?)__/g, "$1")      // Convert __bold__
                                   .replace(/\*([^\*]*?)\*/g, "$1")     // Convert *italic*
                                   .replace(/_([^_]*?)_/g, "$1")        // Convert _italic_
                                   .replace(/\\n/g, "\\n");             // fix EOL
        //console.log(`prepareText('${source}) => ${result}`);
        return result.trim();
    }

    static getHTMLDescription = (monster: Monster): string => {
        const source = '<h>'+ monster.name + '</h>\n' + Abilities.ReplaceTags(monster.desc, monster);
      /*let result = (source ?? '').replace(/&/g, "&amp;")
                                   .replace(/</g, "&lt;")
                                   .replace(/>/g, "&gt;")
                                   .replace(/"/g, "&quot;")
                                   .replace(/'/g, "&apos;");*/
        let result = (source ?? '').replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")// Convert **bold**
                                   .replace(/__(.*?)__/g, "<b>$1</b>")    // Convert __bold__
                                   .replace(/\*(.*?)\*/g, "<i>$1</i>")    // Convert *italic*
                                   .replace(/_(.*?)_/g, "<i>$1</i>")      // Convert _italic_
                                   .replace(/\n\s*\n/g, "<p/>")     // Convert double line breaks into FG paragraphs
                                   .replace(/\n/g, "<br/>");        // Convert single line breaks into <br/>
        //console.log(`prepareText('${source}) => ${result}`);
        //result += monster.image?.src ? `<br/><img src="${monster.image.src}"/>` : '';
        return result.trim();
    }

    // static safeKey = (name: string): string => {
    //     // ensure it starts with a letter and contains only [a-z0-9_]
    //     let key = name.toLowerCase().replace(/[^a-z0-9_]+/g, "_");
    //     if (!/^[a-z]/.test(key)) key = `m_${key}`;
    //     return key;
    // }

    static sanitizeFilename = (name: string): string => {
        return name.replace(/[^a-z0-9_\- ]+/gi, "_");
    }

}